import {
  Badge,
  CardClose,
  CardFooter,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncatedText,
  useIsMobile,
} from '@ais-ui/ui-core';
import {
  DasTargetInfo,
  getTargetInfoName,
  RadarFusedTargetInfo,
  ShipInfo,
  TargetTypeEnum,
} from '@ais-ui/websockets';
import React from 'react';
import { cn } from '@ais-ui/utils';
import { TargetActions } from '../Map/ShipActions/TargetActions';
import { TabsDetails } from './ShipDetailsTabs/TabsDetails';
import {
  PiArrowsClockwise,
  PiArrowSquareOut,
  PiCellSignalSlash,
} from 'react-icons/pi';
import { useViewMode } from '../../components/ViewModeProvider';
import { parseSecondsToCustomDuration } from '../utils/formatDuration';
import { TargetInfoDetails } from './TargetInfoDetails';
import { useTargetContext } from './TargetContext';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSettings } from '../Settings/SettingsProvider';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { AisReportStatus } from './useAISReportAge';
import { useTimeSince } from '../conversions/useTimeSince';
import {
  AIS_DATE_FIELDS,
  AIS_SINCE_FIELD_SOURCE,
  getAisReportDate,
} from './aisReportFields';
import { timeSince } from '@ais-ui/utils';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';
import type { TargetCardTargetType } from '../Settings/Target';
import {
  getFieldDisplayName,
  getFieldIcon,
  TARGET_TYPE_ICONS,
} from '../Settings/Target';

// Map TargetTypeEnum to TargetCardTargetType for settings lookup
const mapTargetTypeToSettingsKey = (
  targetType: TargetTypeEnum,
): TargetCardTargetType => {
  switch (targetType) {
    case TargetTypeEnum.Ship:
    case TargetTypeEnum.ExtendedVessel:
      return 'vessel';
    case TargetTypeEnum.Aton:
      return 'aton';
    case TargetTypeEnum.VirtualAton:
      return 'virtualAton';
    case TargetTypeEnum.Methyd:
      return 'methyd';
    case TargetTypeEnum.BaseStation:
      return 'baseStation';
    case TargetTypeEnum.Radar:
      return 'radar';
    case TargetTypeEnum.RadarFused:
      return 'radarFused';
    case TargetTypeEnum.Das:
      return 'das';
    case TargetTypeEnum.Drone:
      return 'drone';
    default:
      return 'vessel';
  }
};

// Helper function to get field value from target info (supports all target types)
const getFieldValue = (
  targetInfo: any,
  fieldName: string,
  formatDateTimeWithTimeZone?: (date: any) => string,
): string => {
  if (!targetInfo || !fieldName) return '';

  // AIS report datetimes (terrestrial, satellite, and the derived newest message)
  if (AIS_DATE_FIELDS.includes(fieldName as (typeof AIS_DATE_FIELDS)[number])) {
    const date = getAisReportDate(targetInfo, fieldName);
    if (!date) return '';
    return formatDateTimeWithTimeZone ? formatDateTimeWithTimeZone(date) : '';
  }

  // "Time since" for the AIS report datetimes (e.g. "5 minutes ago")
  if (Object.prototype.hasOwnProperty.call(AIS_SINCE_FIELD_SOURCE, fieldName)) {
    const date = getAisReportDate(
      targetInfo,
      AIS_SINCE_FIELD_SOURCE[fieldName],
    );
    return date ? timeSince(date) : '';
  }

  // Date fields that need formatting
  const dateFields = [
    'eta',
    'portEta',
    'lastRadarReport',
    'lastReportTimestamp',
    'reportTimestamp',
  ];

  if (dateFields.includes(fieldName) && formatDateTimeWithTimeZone) {
    const value = targetInfo[fieldName];
    return value ? formatDateTimeWithTimeZone(value) : '';
  }

  // Boolean fields that need special display
  if (fieldName === 'offPosition') {
    return targetInfo.offPosition ? 'Yes' : 'No';
  }

  // Return direct property value with fallback
  const value = targetInfo[fieldName];
  if (value === undefined || value === null) return '';
  return String(value);
};

const SUBTITLE_MAP: Partial<Record<TargetTypeEnum, string>> = {
  [TargetTypeEnum.Das]: 'DAS',
  [TargetTypeEnum.Drone]: 'Drone',
  [TargetTypeEnum.RadarFused]: 'Radar Fused',
};

function getHeaderValue(
  targetInfo: any,
  targetType: TargetTypeEnum | null | undefined,
  headerField: string,
  formatDateTimeWithTimeZone: (date: any) => string,
): string {
  if (targetType === TargetTypeEnum.Das) {
    const dasInfo = targetInfo as DasTargetInfo;
    return `${dasInfo.site || 'Unknown Site'} ${dasInfo.trackId || 'Unknown Track'}`;
  }
  if (targetType === TargetTypeEnum.RadarFused) {
    return getTargetInfoName(targetInfo, targetType) || '';
  }
  return getFieldValue(targetInfo, headerField, formatDateTimeWithTimeZone);
}

function getSubtitleValue(
  targetInfo: any,
  targetType: TargetTypeEnum | null | undefined,
  subtitleField: string,
  formatDateTimeWithTimeZone: (date: any) => string,
): string {
  if (targetType && SUBTITLE_MAP[targetType]) {
    return SUBTITLE_MAP[targetType];
  }
  return getFieldValue(targetInfo, subtitleField, formatDateTimeWithTimeZone);
}

function getTypeBadge(
  targetInfo: any,
  targetType: TargetTypeEnum | null | undefined,
): string | null {
  switch (targetType) {
    case TargetTypeEnum.Ship:
    case TargetTypeEnum.ExtendedVessel:
      return getFieldValue(targetInfo, 'shipType') || 'Unknown Ship Type';
    case TargetTypeEnum.BaseStation:
      return 'Base Station';
    case TargetTypeEnum.Aton:
      return getFieldValue(targetInfo, 'atonType') || 'AtoN';
    case TargetTypeEnum.VirtualAton:
      return (
        getFieldValue(targetInfo, 'virtualAtonType') ||
        getFieldValue(targetInfo, 'atonType') ||
        'Virtual AtoN'
      );
    case TargetTypeEnum.Methyd:
      return 'Met/Hyd';
    case TargetTypeEnum.Radar:
      return 'Radar Target';
    case TargetTypeEnum.RadarFused: {
      const rfInfo = targetInfo as RadarFusedTargetInfo;
      return rfInfo.isFused ? 'AIS + Radar Fused' : 'Radar Only';
    }
    case TargetTypeEnum.Environmental:
      return 'Environmental';
    case TargetTypeEnum.Das:
    case TargetTypeEnum.Drone:
      return null;
    default:
      return null;
  }
}

const TIMELINE_SHIP_FIELDS = [
  'name',
  'imo',
  'mmsi',
  'callSign',
  'country',
  'aisType',
  'sourceType',
  'shipType',
  'width',
  'length',
  'draught',
  'heading',
  'rateOfTurn',
  'courseOverGround',
  'speedOverGround',
  'destination',
  'navigationalStatus',
] as const;

const TIMELINE_DATE_FIELDS = [
  'eta',
  'portEta',
  'lastAisReport',
  'lastAisSatReport',
] as const;

const TIMELINE_COORDINATE_FIELDS = [
  'lastAisLatitude',
  'lastAisLongitude',
  'lastAisSatLatitude',
  'lastAisSatLongitude',
] as const;

function buildTimelinePopupData(
  targetInfo: ShipInfo,
  name: string | undefined,
) {
  const basic =
    'basic' in targetInfo
      ? (targetInfo as unknown as { basic?: Record<string, unknown> }).basic
      : undefined;
  const imo = targetInfo?.imo || basic?.vesselImoNumber;
  const mmsi = targetInfo?.mmsi || basic?.mmsi;
  const vesselName = targetInfo?.name || (basic?.vesselName as string) || name;

  if (!imo) return null;

  const shipData: Record<string, unknown> = {};
  for (const field of TIMELINE_SHIP_FIELDS) {
    shipData[field] = targetInfo?.[field];
  }
  for (const field of TIMELINE_DATE_FIELDS) {
    const val = targetInfo?.[field];
    shipData[field] = (val as any)?.toISOString?.() || val;
  }
  for (const field of TIMELINE_COORDINATE_FIELDS) {
    shipData[field] = targetInfo?.[field];
  }

  return {
    imo: String(imo),
    mmsi: mmsi ? String(mmsi) : '',
    name: vesselName || '',
    shipInfo: JSON.stringify(shipData),
  };
}

function getSettingField(
  setting: unknown,
  settingsKey: string,
  fallback: string,
): string {
  if (typeof setting === 'object' && setting !== null) {
    return (setting as Record<string, string>)[settingsKey] || fallback;
  }
  return (setting as string) || fallback;
}

interface FavoriteFieldItemProps {
  targetInfo: any;
  fieldName: string;
  formatDateTimeWithTimeZone: (date: any) => string;
}

// Renders a single favorite field. "Time since" fields update live via the
// interval-backed hook; everything else is derived directly during render.
const FavoriteFieldItem = ({
  targetInfo,
  fieldName,
  formatDateTimeWithTimeZone,
}: FavoriteFieldItemProps) => {
  const sinceSource = Object.prototype.hasOwnProperty.call(
    AIS_SINCE_FIELD_SOURCE,
    fieldName,
  )
    ? AIS_SINCE_FIELD_SOURCE[fieldName]
    : undefined;
  const sinceDate = sinceSource
    ? getAisReportDate(targetInfo, sinceSource)
    : null;
  const liveSince = useTimeSince(sinceDate);

  const IconComponent = getFieldIcon(fieldName);
  const fieldValue = sinceSource
    ? liveSince
    : getFieldValue(targetInfo, fieldName, formatDateTimeWithTimeZone);

  return (
    <div className="flex items-center gap-2 text-sm">
      <Tooltip>
        <TooltipTrigger className="select-none">
          <IconComponent className="size-4 text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent>{getFieldDisplayName(fieldName)}</TooltipContent>
      </Tooltip>
      <span className="truncate whitespace-nowrap select-text inline-block">
        {fieldValue || '-'}
      </span>
    </div>
  );
};

export const TargetCardContent = () => {
  const {
    canShowDetailedInformation,
    multipleTargetsSelected,
    tab,
    setTab,
    aisReportAgeResult,
    nextTargetName,
    onExit,
    name,
    isExpanded,
    setIsExpanded,
  } = useTargetContext();
  const { currentTargetInfo, currentTargetType, goToNextCard } =
    useSelectionState();
  const { viewMode, setViewMode } = useViewMode();
  const { settings } = useSettings();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const isMobile = useIsMobile();

  // Determine the settings key based on the current target type
  const settingsKey = currentTargetType
    ? mapTargetTypeToSettingsKey(currentTargetType)
    : 'vessel';

  // Get the configured header and subtitle fields from settings (per target type)
  const headerFieldSettings =
    settings['ribbonPlugin.settings']?.target_header_field;
  const subtitleFieldSettings =
    settings['ribbonPlugin.settings']?.target_subtitle_field;
  const favoriteFieldsSettings =
    settings['ribbonPlugin.settings']?.favorite_fields;

  // Support both old (string) and new (object) format for backward compatibility
  const headerField = getSettingField(headerFieldSettings, settingsKey, 'name');
  const subtitleField = getSettingField(
    subtitleFieldSettings,
    settingsKey,
    'callSign',
  );

  // Get the favorite fields from settings (per target type)
  const favoriteFields: string[] =
    typeof favoriteFieldsSettings === 'object' &&
    favoriteFieldsSettings !== null &&
    !Array.isArray(favoriteFieldsSettings)
      ? (favoriteFieldsSettings as Record<string, string[]>)[settingsKey] || []
      : Array.isArray(favoriteFieldsSettings)
        ? favoriteFieldsSettings
        : [];

  // Get the header and subtitle values
  const headerValue = getHeaderValue(
    currentTargetInfo,
    currentTargetType,
    headerField,
    formatDateTimeWithTimeZone,
  );

  const subtitleValue = getSubtitleValue(
    currentTargetInfo,
    currentTargetType,
    subtitleField,
    formatDateTimeWithTimeZone,
  );

  const typeBadge = getTypeBadge(currentTargetInfo, currentTargetType);

  // If no target info or type, return null
  if (!currentTargetInfo || !currentTargetType) {
    return null;
  }

  return (
    <>
      <CardHeader variant="tinted" className="pb-4">
        {/* Exit button - positioned like other panels */}
        {!isMobile && <CardClose onClick={onExit} />}
        {/* Action buttons positioned to the left of close button */}
        {!isMobile && (
          <div className="absolute right-12 top-4 flex items-center gap-3">
            {multipleTargetsSelected && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onClick={goToNextCard}
                  >
                    <PiArrowsClockwise className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {`Go to next target: ${nextTargetName}`}
                </TooltipContent>
              </Tooltip>
            )}
            {canShowDetailedInformation && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      const data = buildTimelinePopupData(
                        currentTargetInfo as ShipInfo,
                        name,
                      );
                      if (data) {
                        openPopupOnSecondaryScreen(
                          PopupType.VesselTimeline,
                          data,
                        );
                      }
                    }}
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <PiArrowSquareOut className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>View timeline and details</TooltipContent>
              </Tooltip>
            )}
            {setIsExpanded && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    {isExpanded ? (
                      <ChevronUp className="size-4" />
                    ) : (
                      <ChevronDown className="size-4" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {isExpanded ? 'Collapse target card' : 'Expand target card'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        <div className="flex justify-between items-start">
          <div className="flex flex-col space-y-2">
            <div className="flex pr-4 space-x-3 items-center">
              <div>
                <CardTitle className="flex text-lg font-medium text-muted-semibold space-x-4">
                  <TruncatedText
                    text={headerValue || 'Unknown'}
                    maxWidth="200px"
                  />
                  {aisReportAgeResult &&
                    aisReportAgeResult.status !== AisReportStatus.Fresh && (
                      <Tooltip>
                        <TooltipTrigger>
                          <PiCellSignalSlash
                            className={`h-6 w-6 ${
                              aisReportAgeResult.status ===
                              AisReportStatus.Removed
                                ? 'text-red-600'
                                : 'text-yellow-600'
                            }`}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          Last AIS message is{' '}
                          {parseSecondsToCustomDuration(
                            aisReportAgeResult.aisReportAge,
                          )}{' '}
                          old
                        </TooltipContent>
                      </Tooltip>
                    )}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{subtitleValue}</p>
              </div>
            </div>
            {typeBadge && currentTargetType && (
              <Badge className="text-xs w-fit space-x-2" variant={'secondary'}>
                {React.createElement(TARGET_TYPE_ICONS[settingsKey], {
                  className: 'h-3 w-3',
                })}
                <p>{typeBadge}</p>
              </Badge>
            )}
          </div>
          <div className="flex flex-col space-y-2 mt-6">
            {currentTargetType === TargetTypeEnum.Ship ||
            currentTargetType === TargetTypeEnum.ExtendedVessel ? (
              <TabsDetails
                tab={tab}
                setTab={setTab}
                imo={
                  currentTargetInfo && 'imo' in currentTargetInfo
                    ? String(currentTargetInfo.imo)
                    : undefined
                }
              />
            ) : null}
          </div>
        </div>
        {/* Separator - Only show if there are favorite fields */}
        {favoriteFields.length > 0 && (
          <div className="pt-3">
            <div className="border-t border-border"></div>
          </div>
        )}

        {/* Favorite Fields - Full width spanning entire card header */}
        {favoriteFields.length > 0 && (
          <div className="pt-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {favoriteFields.map((fieldName: string) => (
                <FavoriteFieldItem
                  key={fieldName}
                  targetInfo={currentTargetInfo}
                  fieldName={fieldName}
                  formatDateTimeWithTimeZone={formatDateTimeWithTimeZone}
                />
              ))}
            </div>
          </div>
        )}
      </CardHeader>

      {/* grid-template-rows 0fr→1fr gives a smooth "paper unrolling" height transition */}
      <div
        className={cn(
          'grid flex-1 min-h-0 transition-[grid-template-rows] duration-300 ease-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden min-h-0 flex flex-col">
          <TargetInfoDetails
            targetInfo={currentTargetInfo}
            targetType={currentTargetType}
            tab={tab}
          />
          {/* Hide action buttons (Create Filter, Last Port Call, etc.) on mobile */}
          {!isMobile && (
            <CardFooter>
              <TargetActions
                targetInfo={currentTargetInfo}
                targetType={currentTargetType}
                viewMode={viewMode}
              />
            </CardFooter>
          )}
        </div>
      </div>
    </>
  );
};
